import React, { useState, useEffect, useRef, useCallback } from 'react';

import classnames from 'classnames';

import { useGoogleMaps } from './useGoogleMaps';

function FadeInHorzLoader(){
  return (<div className="jt-fadein">
    <div className="lds-ellipsis"><div></div><div></div><div></div><div></div></div>
    </div>);
}

function GoogleSuggest(props){

  const { googleMaps, error: loadError } = useGoogleMaps({
    key: props.mapKey,
    libraries: 'places',
  });

  const [input, setInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [apiError, setApiError] = useState(null);
  const sessionTokenRef = useRef(null);
  const timerRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!googleMaps) return;
    sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken();
  }, [googleMaps]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const fetchSuggestions = useCallback(async (value) => {
    if (!googleMaps || value.length < 2) {
      setSuggestions([]);
      return;
    }

    try {
      const { AutocompleteSuggestion } = google.maps.places;
      const result = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input: value,
        sessionToken: sessionTokenRef.current,
      });
      setSuggestions(result.suggestions);
      setIsOpen(result.suggestions.length > 0);
      setActiveIndex(-1);
    } catch (err) {
      setApiError(err);
    }
  }, [googleMaps]);

  const onInputChange = (e) => {
    const value = e.target.value;
    setInput(value);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => fetchSuggestions(value), 400);
  };

  const onSelect = async (suggestion) => {
    const place = suggestion.placePrediction.toPlace();
    await place.fetchFields({ fields: ['location', 'displayName', 'formattedAddress'] });

    props.setPlace({
      placeDesc: place.formattedAddress,
      placeId: place.id,
      latitude: place.location.lat(),
      longitude: place.location.lng(),
    });

    setInput(place.formattedAddress);
    setSuggestions([]);
    setIsOpen(false);
    sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken();
  };

  const onKeyDown = (e) => {
    if (!isOpen) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      onSelect(suggestions[activeIndex]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const onFocus = () => {
    if (suggestions.length > 0) setIsOpen(true);
  };

  const onBlur = () => {
    if (props.onBlur) props.onBlur();
  };

  if( loadError ) throw loadError;
  if( apiError ) throw apiError;

  if( !googleMaps ) return (
    <div className="google-suggest-cont">
      <FadeInHorzLoader/>
    </div>
  );

  const inputClasses = classnames({
    'geosuggest__input':true,
    'form-control':true,
    'form-control-lg':true,
    'is-invalid': props.placeValidation,
  });

  const listClasses = classnames({
    'geosuggest__suggests': true,
    'geosuggest__suggests--hidden': !isOpen,
  });

  return(
    <div className="google-suggest-cont" ref={containerRef}>
      <div className="geosuggest">
        <div className="geosuggest__input-wrapper">
          <input
            id="geosuggest__input"
            className={inputClasses}
            type="text"
            placeholder="Enter a Location"
            value={input}
            onChange={onInputChange}
            onKeyDown={onKeyDown}
            onFocus={onFocus}
            onBlur={onBlur}
            autoComplete="off"
            role="combobox"
            aria-expanded={isOpen}
            aria-owns="geosuggest__list"
            aria-autocomplete="list"
            aria-activedescendant={activeIndex >= 0 ? `geosuggest__item--${activeIndex}` : undefined}
          />
        </div>
        <div className="geosuggest__suggests-wrapper">
          <ul
            id="geosuggest__list"
            className={listClasses}
            role="listbox"
            aria-label="options"
          >
            {suggestions.map((suggestion, index) => {
              const prediction = suggestion.placePrediction;
              const itemClasses = classnames({
                'geosuggest__item': true,
                'geosuggest__item--active': index === activeIndex,
              });
              return (
                <li
                  key={prediction.placeId}
                  id={`geosuggest__item--${index}`}
                  className={itemClasses}
                  role="option"
                  aria-selected={index === activeIndex}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => onSelect(suggestion)}
                  onMouseEnter={() => setActiveIndex(index)}
                >
                  {prediction.text.toString()}
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default GoogleSuggest;
